import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  TextField, Stack, Typography, Chip, Box, Grid
} from '@mui/material'
import StartIcon from '@mui/icons-material/Start'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import MosSmartDialog from '../../../../common/MosSmartDialog'
import createDialogTitle from '../../../../../../generic/utilities/dialogUtil'
import { useSmartTranslation } from '../../../../../../generic/hooks/useSmartTranslation'
import OtherDevicesConfirmDialog from './OtherDevicesConfirmDialog'
import useJobStatusChange from '../../hooks/useJobStatusChange'
import {
  JOB_ACTION_INTENT, JOB_START_MODE, TARGET_STATUS_CODE, resolveTargetStatusCode
} from '../../utils/jobStatus'
import BatchStartModeCards from '../../../../common/batch/BatchStartModeCards'

// Story 113 (S118, §7.4 nº12): the title used to be derived from the target CODE
// (`statusCode.indexOf('ACTIVE') >= 0 ? 'Start' : statusCode`), so any transition between two active
// modes would have been titled "Start". With the intent declared, the title says what the operator
// asked for. A caller that passes no intent keeps the code-derived title, unchanged.
const INTENT_TITLE = {
  [JOB_ACTION_INTENT.GO_TO_EXECUTION]: 'Go to execution',
  [JOB_ACTION_INTENT.BACK_TO_PREPARATION]: 'Back to preparation'
}

const CLOSED_OTHER_DEVICES = { open: false, devices: [] }

// Icon shown in the dialog title, one per target status action.
const STATUS_ICON = {
  [TARGET_STATUS_CODE.START]: StartIcon,
  [TARGET_STATUS_CODE.FINISH]: CheckCircleOutlineIcon,
  [TARGET_STATUS_CODE.INTERRUPT]: PauseCircleOutlineIcon
}

/**
 * Dialog to confirm job status change (e.g., Start job).
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onCancell - Callback when cancelled
 * @param {Function} [props.onSuccess] - Callback when successfully changed
 * @param {Object} props.node - The node/machine information
 * @param {Object} props.jobReference - The job reference
 * @param {Object} props.fabricationOrder - The fabrication order
 * @param {string} [props.statusCode='ACTIVE_EXECUTION'] - The target status code
 * @param {boolean} [props.autoReport=false] - Whether the backend should auto-report the part when the status change is applied
 * @param {string} [props.intent=''] Story 113 (S114, R-F): what the operator is doing, forwarded to the
 *        hook so it can gate the non-productive-task check by intent instead of by target code.
 * @param {boolean} [props.managePreparation=false] Story 113 (S107, C8): the row's switch. With it on,
 *        and only on a START, the dialog asks the operator which mode to open (D-I5). Absent => false,
 *        so a job outside the switch, or an older Sun, behaves exactly as before the story (S111).
 */
const JobStatusChangeDialog = ({
  open,
  onCancell,
  onSuccess,
  node,
  jobReference,
  fabricationOrder,
  statusCode = 'ACTIVE_EXECUTION',
  autoReport = false,
  intent = '',
  managePreparation = false
}) => {
  const { t_ } = useSmartTranslation()

  const {
    comment,
    setComment,
    handleChangeStatus,
    loading,
    AlertElement,
    incompleteConfirm,
    confirmReportIncomplete,
    cancelIncompleteConfirm,
    // Closed-by-default rather than a bare read: this dialog has six call sites and several suites
    // that stub the hook, so a field added later must never be able to blank the screen.
    otherDevicesConfirm = CLOSED_OTHER_DEVICES,
    confirmInterruptOtherDevices,
    cancelInterruptOtherDevices,
    nonProductiveConflict,
    acceptNonProductiveStop,
    cancelNonProductiveStop
  } = useJobStatusChange({ open, onSuccess, onCancell, fabricationOrder, jobReference, autoReport, intent })

  // S107/D-I5: the mode is asked ONLY when the operator is starting (which covers reopening, joining
  // and resuming) AND the row says the job manages preparation. The switch of a job that is not
  // active anywhere can only come from the row (C8): C4/C5 travel for the ACTIVE jobs of this machine
  // only, C1/C2 belong to the technology and POL5 forbids querying it at runtime, and C3 is served by
  // the workflow endpoints alone.
  const offersModeChoice = intent === JOB_ACTION_INTENT.START && managePreparation === true

  // D-I5: PREPARATION comes preselected. Reset on every open so a previous choice never leaks into
  // the next job.
  const [selectedMode, setSelectedMode] = useState(JOB_START_MODE.PREPARATION)
  useEffect(() => {
    if (open) setSelectedMode(JOB_START_MODE.PREPARATION)
  }, [open])

  const actionTitle = useMemo(() => {
    if (offersModeChoice) return t_('How do you want to start this job?')
    if (INTENT_TITLE[intent]) return t_(INTENT_TITLE[intent])
    const actionString = statusCode.indexOf('ACTIVE') >= 0 ? 'Start' : statusCode
    return t_(actionString)
  }, [offersModeChoice, intent, statusCode, t_])

  const TitleIcon = STATUS_ICON[statusCode] || StartIcon

  // S108/S109: confirming the selection IS what asks for the status change -- there is no second
  // confirmation step behind the choice. With no choice offered the code sent is the one the caller
  // asked for, so the pre-story flow is untouched (S111).
  const handleAccept = () => {
    handleChangeStatus(offersModeChoice
      ? resolveTargetStatusCode(JOB_ACTION_INTENT.START, selectedMode)
      : statusCode)
  }

  return (
    <>
      <MosSmartDialog
        title={createDialogTitle({ color: 'primary', title: actionTitle, icon: TitleIcon })}
        setOpen={open}
        cancelCallback={onCancell}
        acceptCallback={handleAccept}
        disableButtons={loading}
      >
        {AlertElement}
        <Stack spacing={2} sx={{ mt: 1, p: (theme) => theme.spacing(2) }}>
          <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1, border: 1, borderColor: 'divider' }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant='caption' color='text.secondary' display='block'>
                  {t_('Fabrication Order')}
                </Typography>
                <Typography variant='subtitle2' fontWeight='bold'>
                  {fabricationOrder?.fabricationOrder || '-'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant='caption' color='text.secondary' display='block'>
                  {t_('Node')}
                </Typography>
                <Typography variant='subtitle2' fontWeight='bold'>
                  {node?.name || '-'}
                </Typography>
              </Grid>
              {!offersModeChoice && (
                <Grid item xs={12}>
                  <Stack direction='row' alignItems='center' spacing={1}>
                    <Typography variant='caption' color='text.secondary'>
                      {t_('Target Status')}:
                    </Typography>
                    <Chip
                      label={t_(statusCode)}
                      color='warning'
                      size='small'
                      variant='filled'
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Stack>
                </Grid>
              )}
            </Grid>
          </Box>
          {/* S107: exactly TWO options and no third one. `Preparation` is the very same key that gives
              text to the station's status label (RN50.1), so the story's new-key count stays at 11.
              Historia 120 (S120): the cards themselves are EXTRACTED to `BatchStartModeCards`, shared
              verbatim with the batch mode question -- pure refactor, this dialog's own behaviour is
              unchanged. */}
          {offersModeChoice && (
            <BatchStartModeCards value={selectedMode} onChange={setSelectedMode} />
          )}
          <TextField
            value={comment}
            label={t_('Comment')}
            placeholder={t_('Provide an optional comment...')}
            onChange={(e) => setComment(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            size='small'
          />
        </Stack>
      </MosSmartDialog>

      {/* Two-step multi-device confirmation: the job is still open on other machines, so finishing it
          here would leave those periods running. Confirming resends the status change asking Sun to
          close them too, in one transaction. Cancelling finishes NOTHING -- the first call wrote
          nothing, so the job stays active everywhere, this machine included.

          The same question is raised for an INTERRUPT asked from a machine that is not running the
          job: pausing it means pausing it on the machines listed, so the operator sees which ones
          first. `statusCode` is the one the hook stored with the question, and only picks the text.

          Mounted BEFORE the incomplete-parts dialog because that is the order of the cascade: the
          multi-device check is a fail-fast pre-check, so a finish that needs both confirmations
          raises this one first and the parts one on the next call.

          Conditional render, like the non-productive dialog below, so the suites that mock the hook
          without these fields keep their silent baseline. */}
      {otherDevicesConfirm.open && (
        <OtherDevicesConfirmDialog
          devices={otherDevicesConfirm.devices}
          statusCode={otherDevicesConfirm.statusCode}
          loading={loading}
          onAccept={confirmInterruptOtherDevices}
          onCancel={cancelInterruptOtherDevices}
        />
      )}

      {/* Two-step NOK confirmation: the backend refused to finish because some parts still have
          material to consume; confirming resends the status change reporting them as NOT OK. */}
      <MosSmartDialog
        title={createDialogTitle({ color: 'warning', title: t_('Finish with incomplete parts?'), icon: WarningAmberRoundedIcon })}
        setOpen={incompleteConfirm.open}
        cancelCallback={cancelIncompleteConfirm}
        acceptCallback={confirmReportIncomplete}
        disableButtons={loading}
        message={t_('{{count}} parts do not have their material fully consumed. Report them as NOT OK and finish?', { count: incompleteConfirm.count })}
      />

      {/* S12 (dir. b, UI-only): joining while the worker has an active non-productive task offers to
          stop it and join in one step. Conditional render so the icon tests (which mock the hook
          without this field) keep their silent baseline. */}
      {nonProductiveConflict && (
        <MosSmartDialog
          title={createDialogTitle({ color: 'warning', title: t_('Active non-productive task'), icon: PauseCircleOutlineIcon })}
          setOpen
          acceptLabel='Stop task and join'
          acceptCallback={acceptNonProductiveStop}
          cancelCallback={cancelNonProductiveStop}
        >
          <Typography sx={{ padding: '1rem' }}>
            {t_('This non productive task is active:')}{' '}
            <Typography component='span' sx={{ fontWeight: 700, color: '#eab308' }}>
              {nonProductiveConflict.task.otherTask?.name || nonProductiveConflict.task.otherTask?.code || ''}
            </Typography>
          </Typography>
        </MosSmartDialog>
      )}
    </>
  )
}

JobStatusChangeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancell: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  node: PropTypes.object,
  jobReference: PropTypes.object,
  fabricationOrder: PropTypes.object,
  statusCode: PropTypes.string,
  autoReport: PropTypes.bool,
  intent: PropTypes.string,
  managePreparation: PropTypes.bool
}

export default JobStatusChangeDialog
